import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall } from './config';
import './WheelchairAdmin.css';
import { useTranslation } from './i18n/LanguageContext';

const WheelchairAdmin = () => {
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    // Stornierte Buchungen standardmäßig ausblenden – sonst sehen sie in der Liste wie
    // Doppelbuchungen aus (20.09.2026: #112/#113 storniert neben #151/#155 auf demselben Rollstuhl).
    const [showCancelled, setShowCancelled] = useState(false);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [editingBooking, setEditingBooking] = useState(null);
    const [editData, setEditData] = useState({});
    const [error, setError] = useState(null);
    
    // Datumsfilter State
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    const { t } = useTranslation();

    useEffect(() => {
        loadData();
        // Heute als Standard setzen
        const today = new Date().toISOString().split('T')[0];
        setFilterStartDate(today);
        setFilterEndDate(today);
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Statistiken laden
            const statsData = await apiCall('/admin/wheelchair/stats');
            console.log('Stats API response:', statsData);
            
            // API gibt {overview: {...}, stations: [...]} zurück
            // Frontend erwartet flache Struktur
            const formattedStats = {
                totalBookings: (statsData.overview?.active_bookings || 0) + (statsData.overview?.completed_bookings || 0),
                todayBookings: 0, // Wird später aus Buchungen berechnet
                totalWheelchairs: statsData.overview?.total_wheelchairs || 0,
                totalStations: statsData.overview?.total_stations || 0
            };
            
            setStats(formattedStats);

            // Buchungen laden
            const bookingsData = await apiCall('/admin/wheelchair/bookings');
            console.log('Bookings API response:', bookingsData);
            
            // Heute-Buchungen berechnen
            // Heute in Münchner Zeit; gespeichert ist das Datum als TTMMJJJJ. Stornierte zählen nicht.
            const iso = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
            const today = iso.slice(8, 10) + iso.slice(5, 7) + iso.slice(0, 4);
            const todayCount = bookingsData.filter(booking =>
                booking.status !== 'cancelled' && (booking.start_date === today || booking.start_date === iso)
            ).length;
            
            setStats(prev => ({...prev, todayBookings: todayCount}));
            setBookings(bookingsData);
            setFilteredBookings(bookingsData);
        } catch (error) {
            console.error('Fehler beim Laden der Daten:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Hilfsfunktionen
    const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        // Verschiedene Formatierung handhaben
        if (dateStr.includes('-')) {
            return dateStr; // Bereits im Format YYYY-MM-DD
        }
        
        // Format: DDMMYYYY zu YYYY-MM-DD
        if (dateStr.length === 8) {
            const day = dateStr.substring(0, 2);
            const month = dateStr.substring(2, 4);
            const year = dateStr.substring(4, 8);
            return `${year}-${month}-${day}`;
        }
        
        return dateStr;
    };

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return '-';
        const formattedDate = formatDateForInput(dateStr);
        if (formattedDate && formattedDate.includes('-')) {
            const [year, month, day] = formattedDate.split('-');
            return `${day}.${month}.${year}`;
        }
        return dateStr;
    };

    const visibleBookings = filteredBookings.filter(b => showCancelled || b.status !== 'cancelled');

    // Filterfunktion für Buchungen
    const applyDateFilter = () => {
        if (!filterStartDate && !filterEndDate) {
            setFilteredBookings(bookings);
            return;
        }

        const filtered = bookings.filter(booking => {
            const bookingDate = formatDateForInput(booking.start_date);
            
            if (filterStartDate && filterEndDate) {
                return bookingDate >= filterStartDate && bookingDate <= filterEndDate;
            } else if (filterStartDate) {
                return bookingDate >= filterStartDate;
            } else if (filterEndDate) {
                return bookingDate <= filterEndDate;
            }
            return true;
        });
        
        setFilteredBookings(filtered);
    };

    // Filter automatisch anwenden wenn sich Daten ändern
    useEffect(() => {
        applyDateFilter();
    }, [bookings, filterStartDate, filterEndDate]);

    const handleEdit = (booking) => {
        setEditingBooking(booking.id);
        setEditData({
            customer_name: booking.customer_name,
            customer_phone: booking.customer_phone || '',
            booking_date: formatDateForInput(booking.start_date),
            start_time: booking.start_time,
            end_time: booking.end_time,
            notes: booking.notes || ''
        });
    };

    const handleCancel = () => {
        setEditingBooking(null);
        setEditData({});
    };

    const handleInputChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async (bookingId) => {
        try {
            await apiCall(`/admin/wheelchair/booking/${bookingId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editData)
            });
            setEditingBooking(null);
            setEditData({});
            loadData(); // Daten neu laden
        } catch (error) {
            console.error('Fehler beim Speichern:', error);
            setError(error.message);
        }
    };

    const handleDelete = async (bookingId) => {
        if (await confirmDialog(t('wc.confirmDelete'))) {
            try {
                await apiCall(`/admin/wheelchair/booking/${bookingId}`, {
                    method: 'DELETE'
                });
                loadData(); // Daten neu laden
            } catch (error) {
                console.error('Fehler beim Löschen:', error);
                setError(error.message);
            }
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{height: '50vh'}}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('wc.loadingData')}</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger m-3">
                <h4>{t('wc.loadError')}</h4>
                <p>{error}</p>
                <button className="btn btn-outline-danger" onClick={loadData}>
                    {t('wc.retry')}
                </button>
            </div>
        );
    }

    return (
        <div className="container-fluid wheelchair-admin">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="page-title">
                            {t('wc.title')}
                        </h2>
                        <button className="btn btn-outline-primary" onClick={loadData}>
                            <i className="bi bi-arrow-clockwise"></i> {t('wc.refresh')}
                        </button>
                    </div>

                    {/* Statistiken */}
                    <div className="row mb-4">
                        <div className="col-lg-3 col-md-6 mb-3">
                            <div className="stat-card stat-card-bookings">
                                <div className="stat-icon">
                                    <i className="bi bi-calendar-check"></i>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{stats.totalBookings || 0}</div>
                                    <div className="stat-label">{t('wc.totalBookings')}</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-3">
                            <div className="stat-card stat-card-today">
                                <div className="stat-icon">
                                    <i className="bi bi-calendar-day"></i>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{stats.todayBookings || 0}</div>
                                    <div className="stat-label">{t('wc.today')}</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-3">
                            <div className="stat-card stat-card-wheelchairs">
                                <div className="stat-icon">
                                    <i className="bi bi-person-wheelchair"></i>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{stats.totalWheelchairs || 0}</div>
                                    <div className="stat-label">{t('wc.wheelchairs')}</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-3">
                            <div className="stat-card stat-card-stations">
                                <div className="stat-icon">
                                    <i className="bi bi-geo-alt"></i>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{stats.totalStations || 0}</div>
                                    <div className="stat-label">{t('wc.stations')}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filter Section */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="mb-0">
                                <i className="bi bi-funnel"></i> {t('wc.filter')}
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-3">
                                    <label className="form-label">{t('wc.fromDate')}</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filterStartDate}
                                        onChange={(e) => setFilterStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">{t('wc.toDate')}</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filterEndDate}
                                        onChange={(e) => setFilterEndDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3 d-flex align-items-end">
                                    <button 
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setFilterStartDate('');
                                            setFilterEndDate('');
                                        }}
                                    >
                                        {t('wc.resetFilter')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Buchungen Tabelle */}
                    <div className="card">
                        <div className="card-header">
                            <h5 className="mb-0">
                                <i className="bi bi-list-ul"></i> {t('wc.bookings')} ({visibleBookings.length})
                                <label className="ms-3 small fw-normal" style={{ cursor: 'pointer' }}>
                                    <input type="checkbox" className="form-check-input me-1" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
                                    stornierte zeigen ({filteredBookings.length - filteredBookings.filter(b => b.status !== 'cancelled').length})
                                </label>
                            </h5>
                        </div>
                        <div className="card-body">
                            {visibleBookings.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-calendar-x display-4 text-muted"></i>
                                    <h5 className="mt-3 text-muted">{t('wc.noBookings')}</h5>
                                    <p className="text-muted">{t('wc.noBookingsForPeriod')}</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>ID</th>
                                                <th>{t('wc.customer')}</th>
                                                <th className="d-none d-md-table-cell">{t('wc.phone')}</th>
                                                <th>{t('wc.date')}</th>
                                                <th>{t('wc.startTime')}</th>
                                                <th>{t('wc.endTime')}</th>
                                                <th>{t('wc.wheelchair')}</th>
                                                <th className="d-none d-md-table-cell">{t('wc.station')}</th>
                                                <th className="d-none d-xl-table-cell">{t('wc.notes')}</th>
                                                <th>{t('wc.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleBookings.map(booking => (
                                                <tr key={booking.id} className={booking.status === 'cancelled' ? 'text-muted' : ''} style={booking.status === 'cancelled' ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
                                                    <td>#{booking.id}{booking.status === 'cancelled' && <span className="badge bg-secondary ms-1" style={{ textDecoration: 'none' }}>storniert</span>}</td>
                                                    <td>
                                                        {editingBooking === booking.id ? (
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={editData.customer_name}
                                                                onChange={(e) => handleInputChange('customer_name', e.target.value)}
                                                            />
                                                        ) : (
                                                            booking.customer_name
                                                        )}
                                                    </td>
                                                    <td className="d-none d-md-table-cell">
                                                        {editingBooking === booking.id ? (
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={editData.customer_phone}
                                                                onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                                                                placeholder={t('wc.phone')}
                                                            />
                                                        ) : (
                                                            booking.customer_phone || '-'
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingBooking === booking.id ? (
                                                            <input
                                                                type="date"
                                                                className="form-control form-control-sm"
                                                                value={editData.booking_date}
                                                                onChange={(e) => handleInputChange('booking_date', e.target.value)}
                                                            />
                                                        ) : (
                                                            formatDateForDisplay(booking.start_date)
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingBooking === booking.id ? (
                                                            <input
                                                                type="time"
                                                                className="form-control form-control-sm"
                                                                value={editData.start_time}
                                                                onChange={(e) => handleInputChange('start_time', e.target.value)}
                                                            />
                                                        ) : (
                                                            booking.start_time
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingBooking === booking.id ? (
                                                            <input
                                                                type="time"
                                                                className="form-control form-control-sm"
                                                                value={editData.end_time}
                                                                onChange={(e) => handleInputChange('end_time', e.target.value)}
                                                            />
                                                        ) : (
                                                            booking.end_time
                                                        )}
                                                    </td>
                                                    <td>{t('wc.wheelchair')} {booking.wheelchair_number}</td>
                                                    <td className="d-none d-md-table-cell">{booking.station_name}</td>
                                                    <td className="d-none d-xl-table-cell">
                                                        {editingBooking === booking.id ? (
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                rows="2"
                                                                value={editData.notes}
                                                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                                                placeholder={t('wc.notes')}
                                                            />
                                                        ) : (
                                                            booking.notes || '-'
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="btn-group-vertical btn-group-sm">
                                                            {editingBooking === booking.id ? (
                                                                <>
                                                                    <button
                                                                        className="btn btn-success btn-sm mb-1"
                                                                        onClick={() => handleSave(booking.id)}
                                                                        title={t('wc.save')}
                                                                    >
                                                                        <i className="bi bi-check-lg"></i>
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-secondary btn-sm"
                                                                        onClick={handleCancel}
                                                                        title={t('wc.cancel')}
                                                                    >
                                                                        <i className="bi bi-x-lg"></i>
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="btn btn-primary btn-sm mb-1"
                                                                        onClick={() => handleEdit(booking)}
                                                                        title={t('wc.edit')}
                                                                    >
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-danger btn-sm"
                                                                        onClick={() => handleDelete(booking.id)}
                                                                        title={t('wc.delete')}
                                                                    >
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WheelchairAdmin;